import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useApp } from "../../context/AppState";
import ProductPageUpsellFormShell from "./ProductPageUpsellFormShell";
import VolumeDiscountWidget, {
  isVolumeDiscountPreview,
} from "../../layouts/volume-discount/VolumeDiscountWidget";
import cartService, {
  setActiveStorefrontTrackingContext,
} from "../../services/cart.service";
import { showCartAddError } from "../../utils/cart-add-toast";
import {
  extractProductPageSlotNoFromElement,
  getProductPageSlotMeta,
  getProductPageSlotNo,
  isProductPageSlotMode,
  queryProductPageSlotElements,
  resolvePopupForProductPageSlot,
} from "../../../shared/product-page-slot.js";
import { isStaticProductPageUpsell } from "../../constants/product-page-upsell-template";
import {
  hasProductPageVisitorTimingGate,
  shouldShowProductPageUpsell,
} from "../../../shared/product-page-upsell-conditions.js";
import { getShopifyCartUrl } from "../../utils/shopify-cart-url";
import {
  getVolumeDiscountOfferUnitCount,
  normalizeVolumeDiscountOfferMode,
  resolveVolumeDiscountLayoutFromPopup,
  volumeDiscountBuyOfferRuleId,
  volumeDiscountGetOfferRuleId,
  VOLUME_DISCOUNT_OFFER_MODE,
} from "../../../shared/volume-discount-template.js";
import { formatProductPriceFields } from "../../helper";
import useEmbeddedUpsellAnalytics from "../../hooks/useEmbeddedUpsellAnalytics";
import { useAnalytics } from "../../hooks/useAnalytics";
import { trackClick } from "../../utils/analyticsUtils";

/** Same cents→major conversion as cart.service / FBT / PriceBlock offer catalog. */
function normalizeProductPricesForDisplay(product) {
  if (!product || typeof product !== "object") return null;
  if (product.__weupsellPricesInMajorUnits === true) return product;
  return {
    ...formatProductPriceFields({ ...product }),
    __weupsellPricesInMajorUnits: true,
  };
}

const PREVIEW_CSS_URL = "https://cdn.weupsell.com/static/css/main.css";

function toCartVariantId(raw) {
  if (raw == null || raw === "") return null;
  const str = String(raw);
  const gidMatch = str.match(/ProductVariant\/(\d+)/i);
  if (gidMatch) return Number(gidMatch[1]);
  const num = Number(str);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function collapseVariantCounts(variantIds) {
  const counts = new Map();
  variantIds.forEach((rawId) => {
    const id = toCartVariantId(rawId);
    if (!id) return;
    counts.set(id, (counts.get(id) || 0) + 1);
  });
  return Array.from(counts.entries()).map(([id, qty]) => ({
    id,
    quantity: qty,
  }));
}

function fallbackVariantId(product) {
  return toCartVariantId(
    product?.variants?.[0]?.id ??
      product?.variant_id ??
      product?.selected_or_first_available_variant?.id
  );
}

/**
 * Read selected Shopify selling_plan from the theme product form.
 * Empty value = one-time purchase (no selling_plan on cart lines).
 */
function readThemeProductFormSellingPlanId(fromEl) {
  if (typeof document === "undefined") return null;

  let form = null;
  if (fromEl?.tagName === "FORM") {
    form = fromEl;
  } else if (fromEl && typeof fromEl.closest === "function") {
    form =
      fromEl.closest('form[action*="/cart/add"]') ||
      fromEl.closest("form");
  }
  if (!form) {
    form = document.querySelector('form[action*="/cart/add"]');
  }
  if (!form) return null;

  const parsePlan = (raw) => {
    const value = String(raw ?? "").trim();
    if (!value) return null;
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  const checkedRadio = form.querySelector(
    'input[type="radio"][name="selling_plan"]:checked'
  );
  if (checkedRadio) return parsePlan(checkedRadio.value);

  const select = form.querySelector('select[name="selling_plan"]');
  if (select) return parsePlan(select.value);

  const input = form.querySelector(
    'input[name="selling_plan"]:not([type="radio"])'
  );
  if (input) return parsePlan(input.value);

  try {
    if (typeof FormData === "function") {
      return parsePlan(new FormData(form).get("selling_plan"));
    }
  } catch (_) {
    /* ignore */
  }

  return null;
}

/**
 * Build cart lines for volume discount ATC.
 * BXGY splits paid (:buy) vs free (:get) so Shopify only discounts free units.
 */
function buildVolumeDiscountCartItems({
  product,
  variantIds,
  quantity,
  offer,
  offerMode,
  sellingPlanId = null,
}) {
  const mode = normalizeVolumeDiscountOfferMode(offerMode);
  const fallback = fallbackVariantId(product);
  const unitCount = getVolumeDiscountOfferUnitCount(offer, mode);
  const units = [];
  const planId =
    sellingPlanId != null && Number(sellingPlanId) > 0
      ? Number(sellingPlanId)
      : null;

  if (Array.isArray(variantIds) && variantIds.length) {
    for (let i = 0; i < Math.max(unitCount, variantIds.length); i += 1) {
      units.push(variantIds[i] ?? variantIds[variantIds.length - 1] ?? fallback);
    }
  } else if (fallback) {
    const qty =
      mode === VOLUME_DISCOUNT_OFFER_MODE.BUY_X_GET_Y
        ? unitCount
        : Math.max(1, Number(quantity) || unitCount || 1);
    for (let i = 0; i < qty; i += 1) units.push(fallback);
  }

  if (!units.length) return [];

  const withPlan = (item) =>
    planId != null ? { ...item, selling_plan: planId } : item;

  if (mode === VOLUME_DISCOUNT_OFFER_MODE.BUY_X_GET_Y && offer?.id) {
    const buyQty = Math.max(0, Number(offer.buyQuantity) || 0);
    const getQty = Math.max(0, Number(offer.getQuantity) || 0);
    const buyUnits = units.slice(0, buyQty);
    const getUnits = units.slice(buyQty, buyQty + getQty);
    const buyRule = volumeDiscountBuyOfferRuleId(offer.id);
    const getRule = volumeDiscountGetOfferRuleId(offer.id);

    return [
      ...collapseVariantCounts(buyUnits).map((item) =>
        withPlan({
          ...item,
          // Paid units: stamp popupId only via a buy rule with no % (unless buy %).
          // Still stamp :buy so lines don't share properties with free units.
          offerRuleId: buyRule,
        })
      ),
      ...collapseVariantCounts(getUnits).map((item) =>
        withPlan({
          ...item,
          offerRuleId: getRule,
        })
      ),
    ];
  }

  return collapseVariantCounts(units).map((item) =>
    withPlan({
      ...item,
      offerRuleId:
        offer?.id != null && String(offer.id) !== ""
          ? String(offer.id)
          : undefined,
    })
  );
}

function VolumeDiscountLiveSlot({
  popup,
  product,
  unitPrice,
  currency,
  onAddToCart,
}) {
  const slotNo = getProductPageSlotNo(popup);
  const { addAnalyticsEvent } = useAnalytics();
  const selectionRef = useRef(null);
  const onAddToCartRef = useRef(onAddToCart);

  useEffect(() => {
    onAddToCartRef.current = onAddToCart;
  }, [onAddToCart]);

  useEmbeddedUpsellAnalytics({
    popupData: popup,
    enabled: true,
    slotNo,
  });

  const trackVolumeClick = async ({ clickTarget, actionType }) => {
    try {
      await trackClick({
        popupId: popup?._id,
        popupType: popup?.template_id?.title || popup?.popUpType,
        campaignId: popup?.campaign_Id,
        clickTarget,
        actionType,
        addAnalyticsEvent,
      });
    } catch (error) {
      console.warn("[VolumeDiscount] click analytics failed:", error);
    }
  };

  const handleOfferSelect = async ({ offer, index }) => {
    await trackVolumeClick({
      clickTarget: "offer",
      actionType: `select_offer_${offer?.id || index}`,
    });
  };

  const handleSelectionChange = useCallback((payload) => {
    selectionRef.current = payload || null;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const api = {
      isReady: () => Boolean(selectionRef.current?.offer),
      getSelection: () => selectionRef.current,
      addToCart: async (options = {}) => {
        const payload = selectionRef.current;
        if (!payload?.offer) {
          showCartAddError("Select a volume offer before adding to cart.");
          return false;
        }
        try {
          await trackClick({
            popupId: popup?._id,
            popupType: popup?.template_id?.title || popup?.popUpType,
            campaignId: popup?.campaign_Id,
            clickTarget: "theme_button",
            actionType: "add_to_cart",
            addAnalyticsEvent,
          });
        } catch (error) {
          console.warn("[VolumeDiscount] theme ATC analytics failed:", error);
        }
        const sellingPlanId =
          options.sellingPlanId != null
            ? options.sellingPlanId
            : readThemeProductFormSellingPlanId(
                options.form || options.eventTarget
              );
        await onAddToCartRef.current?.({
          ...payload,
          sellingPlanId,
        });
        return true;
      },
    };

    window.__WEUPSELL_VOLUME_DISCOUNT_THEME_ATC__ = api;
    return () => {
      if (window.__WEUPSELL_VOLUME_DISCOUNT_THEME_ATC__ === api) {
        delete window.__WEUPSELL_VOLUME_DISCOUNT_THEME_ATC__;
      }
    };
  }, [popup, addAnalyticsEvent]);

  return (
    <VolumeDiscountWidget
      popup={popup}
      product={product}
      unitPrice={unitPrice}
      currency={currency}
      onOfferSelect={handleOfferSelect}
      onSelectionChange={handleSelectionChange}
    />
  );
}

function isDesignMode() {
  if (typeof window === "undefined") return false;
  return Boolean(
    window.weupsellConfig?.designMode ||
      window.weupsellConfig?.visualPreviewMode ||
      window.Shopify?.designMode
  );
}

function ensureShadowContent(host) {
  if (!host) return null;
  const shadow = host.shadowRoot || host.attachShadow({ mode: "open" });

  if (!shadow.querySelector("link[data-weupsell-product-page-css]")) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = PREVIEW_CSS_URL;
    link.setAttribute("data-weupsell-product-page-css", "true");
    shadow.appendChild(link);
  }

  if (!shadow.querySelector("style[data-weupsell-product-page-base]")) {
    const style = document.createElement("style");
    style.setAttribute("data-weupsell-product-page-base", "true");
    style.textContent = `
      :host {
        display: block;
        width: 100%;
        max-width: 100%;
        min-width: 0;
      }
      #weupsell-product-page-slot-content {
        display: block;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
      }
      .weupsell-product-page-upsell-slot {
        display: block;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
        overflow: visible;
      }
    `;
    shadow.appendChild(style);
  }

  let content = shadow.querySelector("#weupsell-product-page-slot-content");
  if (!content) {
    content = document.createElement("div");
    content.id = "weupsell-product-page-slot-content";
    shadow.appendChild(content);
  }

  Object.assign(host.style, {
    display: "block",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  });

  return content;
}

function ProductPageSlotPlaceholder({ slotNo }) {
  const meta = getProductPageSlotMeta(slotNo);
  const title = meta?.blockName || `WeUpsell layout ${slotNo}`;
  const layoutLabel = meta?.label || `layout ${slotNo}`;

  return (
    <div
      style={{
        padding: "14px 16px",
        border: "1px dashed #94a3b8",
        borderRadius: "8px",
        color: "#64748b",
        fontSize: "13px",
        lineHeight: 1.4,
        textAlign: "center",
        background: "#f8fafc",
      }}
    >
      {title}
      <div style={{ marginTop: "4px", fontSize: "12px" }}>
        Create a “{layoutLabel}” campaign in WeUpsell to show it here.
      </div>
    </div>
  );
}

function buildPopupState(popup) {
  const step = popup?.generalSettings?.condition?.embed?.step;
  const activeStep =
    typeof step === "number" && Number.isFinite(step) ? step : 0;

  return {
    activeStep,
    openPopup: true,
    triggerType: "product_page_slot",
    triggeredAt: Date.now(),
    closedAt: null,
  };
}

function resolveProductHandleFromUrl() {
  if (typeof window === "undefined") return "";
  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

async function fetchCurrentProductPageContext() {
  if (typeof window === "undefined") {
    return { product: null, cart: null };
  }

  let product =
    window.__weupsellProduct ||
    window.Shopify?.product ||
    window.meta?.product ||
    null;

  if (!product) {
    const handle = resolveProductHandleFromUrl();
    if (handle) {
      try {
        const res = await fetch(`/products/${handle}.js`, {
          credentials: "same-origin",
        });
        if (res.ok) {
          product = normalizeProductPricesForDisplay(await res.json());
          window.__weupsellProduct = product;
        }
      } catch (_) {
        product = null;
      }
    }
  }

  let cart = null;
  try {
    const res = await fetch(getShopifyCartUrl("cart.js"), {
      credentials: "same-origin",
    });
    if (res.ok) {
      cart = await res.json();
    }
  } catch (_) {
    cart = null;
  }

  return { product, cart };
}

/**
 * Arm visitor-timing triggers (delay / scroll / exit / …).
 * Resolves true when any configured gate fires, or immediately when none set.
 */
function armProductPageVisitorTiming(popup, onUnlock) {
  const trigger = popup?.generalSettings?.condition?.trigger || {};
  if (!hasProductPageVisitorTimingGate(popup)) {
    onUnlock();
    return () => {};
  }

  let unlocked = false;
  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    onUnlock();
  };

  const cleanups = [];

  if (trigger.allow_show_after) {
    const raw = Number(trigger.show_after) || 0;
    const ms = raw * 1000;
    const timer = setTimeout(unlock, Math.max(0, ms));
    cleanups.push(() => clearTimeout(timer));
  }

  if (trigger.allow_after_scroll) {
    const amount = Number(trigger.show_after_scroll) || 0;
    const unit = String(trigger.show_after_scroll_unit || "px").toLowerCase();
    const onScroll = () => {
      const scrolled =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;
      if (unit.includes("%")) {
        const docHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        const pct = docHeight > 0 ? (scrolled / docHeight) * 100 : 0;
        if (pct >= amount) unlock();
      } else if (scrolled >= amount) {
        unlock();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    cleanups.push(() => window.removeEventListener("scroll", onScroll));
  }

  if (trigger.allow_Exit_Intent) {
    const onExit = (event) => {
      if (event.clientY <= 0) unlock();
    };
    document.addEventListener("mouseout", onExit);
    cleanups.push(() => document.removeEventListener("mouseout", onExit));
  }

  if (trigger.allow_after_inactive) {
    const ms = (Number(trigger.show_after_inactive) || 0) * 1000;
    let timer = setTimeout(unlock, Math.max(0, ms));
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(unlock, Math.max(0, ms));
    };
    ["mousemove", "keydown", "scroll", "touchstart"].forEach((evt) => {
      window.addEventListener(evt, reset, { passive: true });
      cleanups.push(() => window.removeEventListener(evt, reset));
    });
    cleanups.push(() => clearTimeout(timer));
  }

  if (trigger.allow_onclick && trigger.onclick_css_selector) {
    const onClick = (event) => {
      const target = event.target;
      if (target?.closest?.(String(trigger.onclick_css_selector))) {
        unlock();
      }
    };
    document.addEventListener("click", onClick);
    cleanups.push(() => document.removeEventListener("click", onClick));
  }

  if (trigger.allow_onhover && trigger.onhover_css_selector) {
    const onHover = (event) => {
      const target = event.target;
      if (target?.closest?.(String(trigger.onhover_css_selector))) {
        unlock();
      }
    };
    document.addEventListener("mouseover", onHover);
    cleanups.push(() => document.removeEventListener("mouseover", onHover));
  }

  return () => {
    cleanups.forEach((fn) => {
      try {
        fn();
      } catch (_) {}
    });
  };
}

const WeupsellProductPageUpsellRuntime = ({ popupValues = [] }) => {
  const { appState, isMobile: viewportIsMobile } = useApp();
  const popups = useMemo(
    () =>
      (Array.isArray(popupValues) ? popupValues : []).filter(
        (popup) =>
          popup &&
          isProductPageSlotMode(popup) &&
          (isStaticProductPageUpsell(popup) || isVolumeDiscountPreview(popup))
      ),
    [popupValues]
  );
  const [portals, setPortals] = useState([]);
  const [pageContext, setPageContext] = useState({
    product: null,
    cart: null,
    ready: false,
  });
  const [visitorUnlocked, setVisitorUnlocked] = useState({});
  const mountedHostsRef = useRef(new Set());
  const visitorTimingCleanupsRef = useRef(new Map());

  useEffect(() => {
    if (appState?.is_preview_mode) {
      return undefined;
    }

    let cancelled = false;
    fetchCurrentProductPageContext().then((ctx) => {
      if (!cancelled) {
        setPageContext({ ...ctx, ready: true });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [appState?.is_preview_mode, popups]);

  const eligiblePopups = useMemo(() => {
    if (!pageContext.ready) return [];
    return popups.filter((popup) =>
      shouldShowProductPageUpsell(popup, {
        product: pageContext.product,
        cart: pageContext.cart,
      })
    );
  }, [popups, pageContext]);

  useEffect(() => {
    if (appState?.is_preview_mode || !pageContext.ready) {
      return undefined;
    }

    eligiblePopups.forEach((popup) => {
      const id = String(popup._id || "");
      if (!id || visitorTimingCleanupsRef.current.has(id)) return;

      const cleanup = armProductPageVisitorTiming(popup, () => {
        setVisitorUnlocked((prev) =>
          prev[id] ? prev : { ...prev, [id]: true }
        );
      });
      visitorTimingCleanupsRef.current.set(id, cleanup);
    });
  }, [appState?.is_preview_mode, pageContext.ready, eligiblePopups]);

  useEffect(() => {
    return () => {
      visitorTimingCleanupsRef.current.forEach((cleanup) => {
        try {
          cleanup();
        } catch (_) {}
      });
      visitorTimingCleanupsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (appState?.is_preview_mode) {
      return undefined;
    }

    let rafId = null;
    let isDisposed = false;
    const designMode = isDesignMode();

    const processProductPageSlots = () => {
      if (isDisposed) return;
      if (!pageContext.ready && !designMode) return;

      const slotElements = queryProductPageSlotElements();
      if (!slotElements.length && !eligiblePopups.length && !designMode) {
        setPortals([]);
        return;
      }

      const nextPortals = [];
      const activeHostKeys = new Set();

      slotElements.forEach((host) => {
        const slotNo = extractProductPageSlotNoFromElement(host);
        if (!slotNo) return;

        const hostKey =
          host.id ||
          `${slotNo}:${host.getAttribute("data-weupsell-prepurchase-slot")}`;
        activeHostKeys.add(hostKey);

        const popup = resolvePopupForProductPageSlot(eligiblePopups, slotNo);
        const content = ensureShadowContent(host);
        if (!content) return;

        mountedHostsRef.current.add(hostKey);

        if (popup) {
          const popupId = String(popup._id || "");
          const timingReady =
            !hasProductPageVisitorTimingGate(popup) ||
            visitorUnlocked[popupId] === true;

          if (!timingReady) {
            return;
          }

          const popupState = buildPopupState(popup);
          nextPortals.push({
            id: `weupsell-product-page-slot-${slotNo}-${popup._id}`,
            hostKey,
            popup,
            content,
            popupState,
            slotNo,
            isPlaceholder: false,
          });
          return;
        }

        if (designMode) {
          nextPortals.push({
            id: `weupsell-product-page-slot-${slotNo}-placeholder`,
            hostKey,
            popup: null,
            content,
            popupState: null,
            slotNo,
            isPlaceholder: true,
          });
        }
      });

      mountedHostsRef.current.forEach((key) => {
        if (!activeHostKeys.has(key)) {
          mountedHostsRef.current.delete(key);
        }
      });

      setPortals(nextPortals);
    };

    const scheduleProcess = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        processProductPageSlots();
      });
    };

    processProductPageSlots();

    const observer = new MutationObserver(scheduleProcess);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["id", "data-weupsell-prepurchase-slot", "class"],
    });

    return () => {
      isDisposed = true;
      if (rafId != null) cancelAnimationFrame(rafId);
      observer.disconnect();
      setPortals([]);
    };
  }, [
    appState?.is_preview_mode,
    eligiblePopups,
    pageContext.ready,
    visitorUnlocked,
  ]);

  if (appState?.is_preview_mode || !portals.length) {
    return null;
  }

  const handleVolumeDiscountAddToCart = async (popup, payload = {}) => {
    const product = pageContext?.product || null;
    const layout = resolveVolumeDiscountLayoutFromPopup(popup);
    const offerMode = layout?.offerMode;
    const sellingPlanId =
      payload.sellingPlanId != null
        ? payload.sellingPlanId
        : readThemeProductFormSellingPlanId();
    const items = buildVolumeDiscountCartItems({
      product,
      variantIds: payload.variantIds,
      quantity: payload.quantity,
      offer: payload.offer,
      offerMode,
      sellingPlanId,
    });

    if (!items.length) {
      showCartAddError("Could not add to cart — no product variant found.");
      return;
    }

    try {
      setActiveStorefrontTrackingContext({
        popup,
        shop: window?.Shopify?.shop || window.location.hostname,
        slotNo: getProductPageSlotNo(popup),
      });
      await cartService.addToCart(items, {
        attachTracking: true,
        syncCartAfterAdd: true,
        suppressModalOnCartAddRefire: true,
        // Theme ATC replace sets VOLUME_ATC_HANDLED — use native fetch so our
        // own cart/add is not swallowed by the "pack already added" guard.
        bypassCartFetchPatch: true,
      });
    } catch (error) {
      console.error("[VolumeDiscount] Add to cart failed:", error);
      showCartAddError(
        error?.message || "Could not add to cart. Please try again."
      );
    }
  };

  return (
    <>
      {portals.map(
        ({ id, popup, content, popupState, slotNo, isPlaceholder }) => {
          if (isPlaceholder) {
            return createPortal(
              <ProductPageSlotPlaceholder slotNo={slotNo} />,
              content,
              id
            );
          }

          if (isVolumeDiscountPreview(popup)) {
            const product = normalizeProductPricesForDisplay(
              pageContext?.product || null
            );
            const unitPrice =
              Number(
                product?.variants?.[0]?.price ?? product?.price ?? 30
              ) || 30;
            const currency =
              product?.currency ||
              window.Shopify?.currency?.active ||
              "USD";
            return createPortal(
              <VolumeDiscountLiveSlot
                popup={popup}
                product={product}
                unitPrice={unitPrice}
                currency={currency}
                onAddToCart={(payload) =>
                  handleVolumeDiscountAddToCart(popup, payload)
                }
              />,
              content,
              id
            );
          }

          return createPortal(
            <ProductPageUpsellFormShell
              popupData={popup}
              activeStep={popupState.activeStep}
              isMobile={viewportIsMobile}
              popupState={popupState}
            />,
            content,
            id
          );
        }
      )}
    </>
  );
};

export default WeupsellProductPageUpsellRuntime;
